import { MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import { type Response } from 'core/api'
import { useEffect, useState } from 'react'
import { NotificationManager } from 'react-notifications'
import { userNameComparator } from '../../core/utils'
import { ReviewType } from '../../models/review-data'
import { ReviewStore, UserStore } from '../../stores'

interface Props {
  reviewType: ReviewType
  testRunId: number
  testRunReviewerUserId: number | undefined
  testRunTestId: number
  testRunTestReviewerUserId: number | undefined
}

const AssignReviewUserSelect = ({ reviewType, testRunId, testRunReviewerUserId, testRunTestId, testRunTestReviewerUserId }: Props): JSX.Element => {
  const [value, setValue] = useState<string>('')
  const users = UserStore.usersForDropdown
  const options = users.sort((a, b) => userNameComparator(a, b)).map(user => {
    return <MenuItem key={user.id} value={user.id}>{user.fullName}</MenuItem>
  })

  useEffect(() => {
    let currentUserId: number | null | undefined
    switch (reviewType) {
      case ReviewType.TEST_RUN:
        currentUserId = testRunReviewerUserId
        break
      case ReviewType.TEST_RUN_TEST:
        currentUserId = testRunTestReviewerUserId
        break
    }

    const value = users.find(user => user.id === currentUserId)?.id
    setValue(value == null ? '' : value.toString())
  }, [users, reviewType, testRunReviewerUserId, testRunTestReviewerUserId])

  async function handleChange (e: SelectChangeEvent<string>): Promise<void> {
    const assigneeUserId = e.target.value
    const numericUserId = assigneeUserId !== '' ? Number(assigneeUserId) : null

    if (reviewType === ReviewType.TEST_RUN) {
      if (numericUserId === (testRunReviewerUserId ?? null)) { return }

      testRunReviewerUserId = numericUserId ?? undefined
    } else {
      if (numericUserId === (testRunTestReviewerUserId ?? null)) { return }

      testRunTestReviewerUserId = numericUserId ?? undefined
    }

    setValue(assigneeUserId)

    const response: Response<any> | undefined = await ReviewStore.updateAssignedReviewer({
      reviewType,
      testRunId,
      testRunTestId,
      assigneeUserId: numericUserId ?? undefined
    })
      .catch(() => NotificationManager.error('Please try again', 'Something went wrong'))

    let message
    if (reviewType === ReviewType.TEST_RUN) { message = 'Test run successfully assigned for review' } else { message = 'Test run test successfully assigned for review' }

    if (response != null && response.status === 200) { NotificationManager.success(message, 'Success', 2000) }
  }

  return (
    <>
      <Select style={{ background: '#FFF', textAlign: 'left', width: '200px' }}
              displayEmpty
              value={value}
              onChange={(e) => { void handleChange(e) }}>
        <MenuItem key={''} value={''}><em>Assign Reviewer (None)</em></MenuItem>
        {options}
      </Select>
    </>
  )
}

export default AssignReviewUserSelect
